<template>
    <o-table :title="$t('sandoz.pass_through_controls')" ref="tableList" url="/api/pass_through_controls" :enable-new="false"
             :enable-delete="false" :enable-edit="true" :enable-view="false"
             default-sort-by-direction="desc" default-sort-by="id" :show-overlay="true">
        <template slot="filter">
          <div class="d-flex">
            <o-filter v-on:filter-updated="(filters) => $refs.tableList.setFilters(filters)" :filter-cacheable="true">
              <o-filter-select :items="dwhWholesalers" :title="$t('sandoz.wholesaler')" column="whole_saler" value-field="dwh_name" text-field="dwh_name"></o-filter-select>
              <o-filter-select :items="statuses" :title="$t('sandoz.status')" column="status" value-field="statusCode" text-field="statusName"></o-filter-select>
              <o-filter-select :items="buyingGroups" :title="$t('sandoz.buying_group')" column="buying_group" value-field="buying_group_code" text-field="buying_group"></o-filter-select>
              <o-filter-text :title="$t('sandoz.customer_external_id')" column="external_id"></o-filter-text>
              <o-filter-text :title="$t('sandoz.customer_ean')" column="national_id"></o-filter-text>
              <o-filter-text :title="$t('sandoz.package_eancode')" column="package_eancode"></o-filter-text>
              <o-filter-monthpicker :title="$t('sandoz.date')" column="id_time"></o-filter-monthpicker>
              <o-filter-select :items="kukoExistence" :title="$t('sandoz.kuko_existence')" column="kuko_existence"></o-filter-select>
              <o-filter-select :items="customerTypes" :title="$t('sandoz.customer_type')" column="contact_sub_type"></o-filter-select>
              <o-filter-select :items="materialGroups" :title="$t('sandoz.material_group')" column="product_group" value-field="product_group" text-field="product_group"></o-filter-select>
              <o-filter-select :items="verificationItems" :title="$t('sandoz.verified')" column="verified"></o-filter-select>
            </o-filter>
            <div class="d-flex">
              <div v-if="lastRefreshDate" class="mr-3 mt-1">
                {{ lastRefreshDate | formatDate}}
              </div>
              <div class="form-check pt-1 mr-3">
                <input class="form-check-input" type="checkbox" id="showValues" v-model="showValues">
                <label class="form-check-label text-nowrap" for="showValues">
                  {{ $t('sandoz.show_absolute_values') }}
                </label>
              </div>
              <div class="mr-3">
                <router-link :to="{ name: 'sandoz.pass_through_blacklist' }" class="btn btn-primary text-nowrap">
                  {{ $t('sandoz.new_from_blacklist_report') }}
                </router-link>
              </div>
              <div>
                <o-button @click="exportToExcel" :loading="exporting">XLSX</o-button>
              </div>
            </div>
          </div>
        </template>
        <template slot="head">
            <o-table-header column="national_id">{{$t('sandoz.customer_ean')}}</o-table-header>
            <o-table-header column="external_id">{{$t('sandoz.customer_external_id')}}</o-table-header>
            <o-table-header column="full_name">{{$t('sandoz.full_name')}}</o-table-header>
            <o-table-header column="id_time">{{$t('sandoz.date')}}</o-table-header>
            <o-table-header v-if="showValues" class="text-right" column="quantity">{{$t('sandoz.quantity')}}</o-table-header>
            <o-table-header v-if="showValues" class="text-right" column="value">{{$t('sandoz.value')}}</o-table-header>
            <o-table-header v-if="showValues" class="text-right" column="discount_value">{{$t('sandoz.discount_value')}}</o-table-header>
            <o-table-header class="text-right" column="discount_percentage">{{$t('sandoz.discount_percentage')}}</o-table-header>
            <o-table-header class="text-right" column="max_kuko_discount">{{$t('sandoz.max_kuko_discount')}}</o-table-header>
            <o-table-header :sortable="false">{{$t('sandoz.status')}}</o-table-header>
            <o-table-header column="whole_saler">{{$t('sandoz.wholesaler')}}</o-table-header>
            <o-table-header column="package_code">{{$t('sandoz.package_code')}}</o-table-header>
            <o-table-header column="package">{{$t('sandoz.package')}}</o-table-header>
            <o-table-header column="package_eancode">{{$t('sandoz.package_eancode')}}</o-table-header>
            <o-table-header column="kukos" :sortable="false">{{$t('sandoz.kukos')}}</o-table-header>
            <o-table-header column="verified" :sortable="false">{{$t('sandoz.verified')}}</o-table-header>
            <o-table-header column="note" :sortable="false">{{$t('sandoz.note')}}</o-table-header>
            <o-table-header column="file_name" :sortable="false">{{$t('core.attachments')}}</o-table-header>
        </template>
        <template slot="body" slot-scope="props">
            <td>{{ props.item.national_id }}</td>
            <td>{{ props.item.external_id }}</td>
            <td>{{ props.item.full_name }}</td>
            <td>{{ props.item.id_time }}</td>
            <td v-if="showValues" class="text-right">{{ props.item.quantity }}</td>
            <td v-if="showValues" class="text-right">{{ props.item.value }}</td>
            <td v-if="showValues" class="text-right">{{ props.item.discount_value }}</td>
            <td class="text-right">{{ props.item.discount_percentage * 100 }}%</td>
            <td class="text-right" :class="{'text-danger': (props.item.max_kuko_discount || 0) != (props.item.discount_percentage || 0)}">{{ props.item.max_kuko_discount * 100 }}%</td>
            <td>
              <span class="badge badge-danger" v-if="(props.item.max_kuko_discount || 0) != (props.item.discount_percentage || 0)">{{$t('sandoz.different')}}</span>
              <span class="badge badge-success" v-else>{{$t('sandoz.same')}}</span>
            </td>
            <td>{{ props.item.whole_saler }}</td>
            <td>{{ props.item.package_code }}</td>
            <td>{{ props.item.package }}</td>
            <td>{{ props.item.package_eancode }}</td>
            <td>
              <span v-if="props.item.kuko_ids">
                <span v-for="(kukoId, kukoIndex) in props.item.kuko_ids.split(',')" :key="kukoId">
                  <span v-if="kukoIndex > 0">, </span>
                  <a href="javascript:void(0)" @click="gotoKuko(kukoId)">
                    {{kukoId}}
                  </a>
                </span>
              </span>
            </td>
            <td>
              <template v-if="checkSaleDetail(props.item)">
                  <fa icon="check"/>
              </template>
              <template v-else>
                  <fa icon="times"/>
              </template>
            </td>
            <td>
              {{  props.item.note }}
            </td>
            <td>
              <fa icon="download" v-if="props.item.file_name" @click="downloadFile(props.item.file_name)"/>
            </td>
        </template>
    </o-table>
</template>

<script>
import axios from 'axios'

export default {
  data: () => ({
    dwhWholesalers: [],
    buyingGroups: [],
    statuses: [],
    showValues: false,
    exporting: false,
    lastRefreshDate : null,
    kukoExistence: [],
    customerTypes: [],
    materialGroups:[],
    consolidatedSaleUsers:null,
    verificationItems: []
  }),
  mounted() {
    this.loadConsolidatedDetails()
    this.statuses.push({statusCode: 'same', statusName: this.$i18n.t('sandoz.same')})
    this.statuses.push({statusCode: 'different', statusName: this.$i18n.t('sandoz.different')})

    this.kukoExistence.push({value: 'yes', text: this.$i18n.t('sandoz.yes')})
    this.kukoExistence.push({value: 'no', text: this.$i18n.t('sandoz.no')})

    this.verificationItems.push({value: 'yes', text: this.$i18n.t('sandoz.yes')})
    this.verificationItems.push({value: 'no', text: this.$i18n.t('sandoz.no')})

    this.customerTypes.push({value: 'Administration', text: 'Administration'});
    this.customerTypes.push({value: 'Dental Clinic', text: 'Dental Clinic'});
    this.customerTypes.push({value: 'Dentist', text: 'Dentist'});
    this.customerTypes.push({value: 'Doctor', text: 'Doctor'});
    this.customerTypes.push({value: 'Doctor Chain', text: 'Doctor Chain'});
    this.customerTypes.push({value: 'Doctor in Hospital', text: 'Doctor in Hospital'});
    this.customerTypes.push({value: 'Doctor in Hospital/Practice', text: 'Doctor in Hospital/Practice'});
    this.customerTypes.push({value: 'Doctor in Practice', text: 'Doctor in Practice'});
    this.customerTypes.push({value: 'Doctor Network', text: 'Doctor Network'});
    this.customerTypes.push({value: 'Drugstore', text: 'Drugstore'});
    this.customerTypes.push({value: 'Group Practice', text: 'Group Practice'});
    this.customerTypes.push({value: 'Individual Practice', text: 'Individual Practice'});
    this.customerTypes.push({value: 'Insurance Institution', text: 'Insurance Institution'});
    this.customerTypes.push({value: 'NGO', text: 'NGO'});
    this.customerTypes.push({value: 'Nurse', text: 'Nurse'});
    this.customerTypes.push({value: 'Nursing Home', text: 'Nursing Home'});
    this.customerTypes.push({value: 'Nursing Home with Pharmacy', text: 'Nursing Home with Pharmacy'});
    this.customerTypes.push({value: 'Nursing Home without Pharmacy', text: 'Nursing Home without Pharmacy'});
    this.customerTypes.push({value: 'Online Pharmacy', text: 'Online Pharmacy'});
    this.customerTypes.push({value: 'Other Customer', text: 'Other Customer'});
    this.customerTypes.push({value: 'Other HCO', text: 'Other HCO'});
    this.customerTypes.push({value: 'Other Institution', text: 'Other Institution'});
    this.customerTypes.push({value: 'Other Non-Prescribing HCP', text: 'Other Non-Prescribing HCP'});
    this.customerTypes.push({value: 'Other Personnel', text: 'Other Personnel'});
    this.customerTypes.push({value: 'Other Staff', text: 'Other Staff'});
    this.customerTypes.push({value: 'Pharmacist', text: 'Pharmacist'});
    this.customerTypes.push({value: 'Pharmacy Buying Group', text: 'Pharmacy Buying Group'});
    this.customerTypes.push({value: 'Pharmacy Chain', text: 'Pharmacy Chain'});
    this.customerTypes.push({value: 'Pharmacy Personnel', text: 'Pharmacy Personnel'});
    this.customerTypes.push({value: 'Public Pharmacy', text: 'Public Pharmacy'});
    this.customerTypes.push({value: 'Radiology Center', text: 'Radiology Center'});
    this.customerTypes.push({value: 'Undef', text: 'Undef'});
    this.customerTypes.push({value: 'Vet', text: 'Vet'});
    this.customerTypes.push({value: 'Vet Clinic', text: 'Vet Clinic'});
    this.customerTypes.push({value: 'Wholesaler', text: 'Wholesaler'});

    axios.get(`/api/pass_through_controls/wholesalers`).then(response => {
      this.dwhWholesalers = response.data
    })
    axios.get(`/api/buying_groups?per_page=9999`).then(response => {
      this.buyingGroups = response.data.data
    })

    axios.get(`/api/pass_through_controls/last_refresh_date`).then(response => {
      this.lastRefreshDate = response.data
    })

    axios.get(`/api/kuko_product_groups?per_page=9999`).then(response => {
      this.materialGroups = response.data.data
    })
  },
  methods: {
    exportToExcel () {
      if (this.$refs.tableList.data.meta.pagination.total > 100000) {
        window.alert('Too many records to export, please set more filter conditions')
        return
      }
      const _self = this
      _self.exporting = true
      _self.$nextTick(() => {
        _self.$refs.tableList.downloadExcel().then((response) => {
          _self.exporting = false
        })
      })
    },
    gotoKuko (id) {
      const route = this.$router.resolve({ path: '/kukos/' + id })
      window.open(route.href)
    },
    loadConsolidatedDetails () {
      axios.get(`/api/consolidate_sales_users?per_page=9999`).then(response => {
        this.consolidatedSaleUsers = response.data.data
      })
    },
    checkSaleDetail(item) {
      if(this.consolidatedSaleUsers && this.consolidatedSaleUsers.length > 0) {
        let filterSales = this.consolidatedSaleUsers.filter((detail) => {
          let id_time = parseInt(item.id_time_original)
          return detail.external_id === item.external_id && detail.id_time === id_time && detail.package_eancode === item.package_eancode
        });

        if(filterSales.length > 0) {
          item.note = filterSales[0].note
          item.file_name = filterSales[0].file_name
          item.verified = filterSales[0].verified
          return filterSales[0].verified
        }
      }

      return false
    },
    downloadFile(filename) {
      var data = {
        file: filename
      }
      axios.post(`/api/consolidate_sales_users/download`, data, { responseType: 'arraybuffer' }).then(response => {
          require('downloadjs')(response.data, filename.name)
      })
    }
  }
}
</script>
